// src/components/Header.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../context/AppContext';
import { useTheme } from '../context/AppContext';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import WaveHeader from './WaveHeader';
export default function Header() {
  const { t, toggleLang } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Закрываем меню при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden'; // запрещаем скролл при открытом меню
    } else {
      document.body.style.overflow = ''; // разрешаем скролл
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const isActive = (path) =>
    location.pathname === path
      ? 'font-bold text-blue-300 md:text-white'
      : 'text-gray-200 hover:text-white';

  return (
<motion.header 
  className="relative bg-gradient-to-br from-blue-600 to-indigo-800 text-white px-6 overflow-hidden"
  initial={{ opacity: 0, y: -100 }}
  animate={{
    opacity: 1,
    y: 0,
    paddingTop: location.pathname === '/' ? '1.5rem' : '1rem',
    paddingBottom: location.pathname === '/' ? '3rem' : '1rem',
  }}
  transition={{ 
    duration: 0.6, 
    ease: 'easeInOut'
  }}
>

  {/* Волна внизу хедера */}
  <WaveHeader className="text-blue-600 dark:text-indigo-900" />

  {/* Контент поверх фона */}
  <div className="max-w-7xl mx-auto flex justify-between items-center relative z-9">
    <Link to="/" className="flex items-center">
      <img
        src="/logo.svg"
        alt="Sonifly Logo"
        className="h-8 md:h-10 mr-2 transition-transform hover:scale-105"
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'block';
        }}
      />
      <span className="text-xl md:text-2xl font-bold hidden">Sonifly</span>
    </Link>

    {/* Бургер-меню для мобильных */}
    <button
      className="md:hidden text-white focus:outline-none z-30"
      onClick={() => setIsMenuOpen(true)}
      aria-label="Open menu"
    >
      ☰
    </button>

    {/* Оверлей (затемнение фона) */}
    {isMenuOpen && (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
        onClick={() => setIsMenuOpen(false)}
      ></div>
    )}

    {/* Боковое меню */}
    <div
      ref={menuRef}
      className={`${
        isMenuOpen ? 'translate-x-0' : '-translate-x-full'
      } fixed top-0 left-0 h-full w-64 bg-blue-600 dark:bg-gray-900 text-white p-6 pt-16 z-[9999] flex flex-col gap-6 transition-transform duration-300 ease-in-out md:hidden shadow-2xl`}
    >
      <button
        className="absolute top-4 right-4 text-white text-2xl focus:outline-none"
        onClick={() => setIsMenuOpen(false)}
        aria-label="Close menu"
      >
        ✕
      </button>

      <nav className="flex flex-col space-y-4 mt-4">
        <Link
          to="/"
          className={isActive('/')}
          onClick={() => setIsMenuOpen(false)}
        >
          {t.home}
        </Link>
        <Link
          to="/instruction"
          className={isActive('/instruction')}
          onClick={() => setIsMenuOpen(false)}
        >
          {t.instruction}
        </Link>
      </nav>

      <div className="mt-auto pt-4 border-t border-gray-300 dark:border-gray-700">
        <button
          onClick={() => {
            toggleLang();
            setIsMenuOpen(false);
          }}
          className="block text-white underline text-sm hover:text-gray-200 transition mb-3"
        >
          {t.langSwitch}
        </button>
        <button
          onClick={() => {
            toggleTheme();
            setIsMenuOpen(false);
          }}
          className="block text-white text-sm hover:text-gray-200 transition"
          aria-label={
            theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'
          }
        >
          {theme === 'dark' ? t.lightTheme : t.darkTheme}
        </button>
      </div>
    </div>

    {/* Десктопное меню */}
    <nav className="hidden md:flex md:items-center md:space-x-6">
      <Link to="/" className={isActive('/')}>
        {t.home}
      </Link>
      <Link to="/instruction" className={isActive('/instruction')}>
        {t.instruction}
      </Link>
      <button
        onClick={toggleLang}
        className="text-white underline text-sm hover:text-gray-200 transition"
      >
        {t.langSwitch}
      </button>
      <button
        onClick={toggleTheme}
        className="text-white text-sm hover:text-gray-300 transition"
        aria-label={
          theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'
        }
      >
        {theme === 'dark' ? t.lightTheme : t.darkTheme}
      </button>
    </nav>
  </div>

  {/* Hero-секция с анимацией только на главной */}
  <motion.div 
    className="max-w-5xl mx-auto text-center relative z-10 overflow-hidden"
    initial={{ opacity: 0 }}
    animate={{
      opacity: location.pathname === '/' ? 1 : 0,
      height: location.pathname === '/' ? 'auto' : 0,
      marginTop: location.pathname === '/' ? '3rem' : 0,
    }}
    transition={{ 
      duration: 0.6, 
      ease: 'easeInOut',
      delay: 0.2
    }}
  >
    {/* <h1 className="text-4xl md:text-6xl font-bold mb-6">{t.title}</h1> */}
    <p className="text-xl md:text-2xl mb-8 opacity-90">{t.subtitle}</p>
    <Link
      to="/#download"
      className="inline-block bg-white text-blue-700 font-semibold py-3 px-8 rounded-full shadow-lg hover:bg-gray-100 transition transform hover:scale-105 dark:bg-gray-700 dark:text-blue-300 dark:hover:bg-gray-600"
    >
      {t.downloadBtn}
    </Link>
  </motion.div>
</motion.header>
  );
}